import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { VideoSection } from "@/components/VideoSection";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { TrackLocations } from "@/hooks/use-segment";
import { Text } from "@/components/podkit/typography/Text";
import { cn } from "@/components/podkit/lib/cn";
import { IconArrowUpRight } from "central-icons/IconArrowUpRight";
import { IconChevronRight } from "central-icons/IconChevronRight";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import type { CentralIconBaseProps } from "central-icons";
import { IconSparkle } from "central-icons/IconSparkle";
import { IconZap } from "central-icons/IconZap";
import { IconBug } from "central-icons/IconBug";
import type { ChangelogData, ChangelogSections } from "@/hooks/use-changelog";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

/** Extract a Mux playback ID from a Mux thumbnail URL, or return null. */
function parseMuxPlaybackId(url: string): string | null {
    const match = /image\.mux\.com\/([^/]+)\//.exec(url);
    return match?.[1] ?? null;
}

type ChangelogDialogProps = {
    changelog: ChangelogData;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const docsBaseUrl = "https://docs.ona.com";

const markdownComponents: Components = {
    p: ({ children }) => <p className="mb-3 text-base leading-relaxed text-content-primary">{children}</p>,
    h1: ({ children }) => <h1 className="mb-3 text-xl font-bold text-content-primary">{children}</h1>,
    h2: ({ children }) => <h2 className="mb-2 text-lg font-bold text-content-primary">{children}</h2>,
    h3: ({ children }) => <h3 className="mb-2 text-base font-bold text-content-primary">{children}</h3>,
    ul: ({ children }) => <ul className="mb-3 flex list-disc flex-col gap-1.5 pl-5">{children}</ul>,
    ol: ({ children }) => <ol className="mb-3 flex list-decimal flex-col gap-1.5 pl-5">{children}</ol>,
    li: ({ children }) => <li className="text-base leading-relaxed text-content-secondary">{children}</li>,
    a: ({ children, href }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-content-link hover:underline">
            {children}
        </a>
    ),
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => <code className="rounded bg-surface-02 px-1 py-0.5 font-mono text-sm">{children}</code>,
    blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-border-light pl-4 text-content-secondary">{children}</blockquote>
    ),
};

const sectionMeta: {
    key: keyof ChangelogSections;
    label: string;
    icon: FC<CentralIconBaseProps>;
    color: string;
}[] = [
    { key: "new", label: "New", icon: IconSparkle, color: "text-content-secondary" },
    { key: "improvements", label: "Improvements", icon: IconZap, color: "text-content-secondary" },
    { key: "bugFixes", label: "Bug fixes", icon: IconBug, color: "text-content-secondary" },
];

/** Collapsible section row — icon, label, chevron, animated content. */
const CollapsibleSection: FC<{
    label: string;
    icon: FC<CentralIconBaseProps>;
    color: string;
    items: string[];
    defaultOpen?: boolean;
}> = ({ label, icon: Icon, color, items, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild>
                <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-left hover:bg-surface-hover"
                >
                    <Icon size={16} className={cn("shrink-0", color)} />
                    <Text className="flex-1 text-base font-semibold text-content-primary">{label}</Text>
                    <span className="text-content-tertiary">
                        <IconChevronRight
                            size={14}
                            className={cn("shrink-0 transition-transform duration-200", open && "rotate-90")}
                        />
                    </span>
                </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <ul className="flex flex-col gap-2 pb-1 pl-7 pt-1">
                    {items.map((item) => (
                        <li
                            key={item}
                            className="list-disc text-base leading-relaxed text-content-primary marker:text-content-tertiary"
                        >
                            {item}
                        </li>
                    ))}
                </ul>
            </CollapsibleContent>
        </Collapsible>
    );
};

/** "What else we shipped" wrapper with collapsible per-category sections. */
const ChangelogSectionsBlock: FC<{ sections: ChangelogSections }> = ({ sections }) => {
    const hasSections = sectionMeta.some(({ key }) => (sections[key]?.length ?? 0) > 0);
    if (!hasSections) return null;

    return (
        <div className="mt-5 border-t border-border-subtle pt-4">
            <Text className="mb-3 text-md font-bold text-content-strong">What else we&apos;ve shipped</Text>
            <div className="flex flex-col gap-0.5">
                {sectionMeta.map(({ key, label, icon, color }) => {
                    const items = sections[key];
                    if (!items?.length) return null;
                    return (
                        <CollapsibleSection
                            key={key}
                            label={label}
                            icon={icon}
                            color={color}
                            items={items}
                            defaultOpen={false}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export const ChangelogDialog: FC<ChangelogDialogProps> = ({ changelog, open, onOpenChange }) => {
    const [imgFailed, setImgFailed] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const handleImgError = useCallback(() => setImgFailed(true), []);
    const handleVideoReady = useCallback(() => setVideoReady(true), []);
    const muxPlaybackId = useMemo(() => parseMuxPlaybackId(changelog.imageUrl), [changelog.imageUrl]);

    // Counter incremented on each open — used as a key to remount VideoSection
    // so it restarts from 0:00 and autoplays again.
    const openCountRef = useRef(0);
    useEffect(() => {
        if (open) {
            openCountRef.current += 1;
        } else {
            setVideoReady(false);
        }
    }, [open]);

    return (
        <>
            {/* Hidden preload image — keeps the hero image in the browser's
                decoded cache so it paints instantly when the dialog opens. */}
            {changelog.imageUrl && (
                <img
                    src={changelog.imageUrl}
                    alt=""
                    aria-hidden
                    className="absolute size-0 overflow-hidden opacity-0"
                />
            )}

            <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
                <DialogPrimitive.Portal forceMount>
                    {open && (
                        <>
                            {/* Overlay */}
                            <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-surface-primary/50 backdrop-blur-modal backdrop-opacity-modal animate-in fade-in-0" />
                            {/* Content */}
                            <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                                <DialogPrimitive.Content
                                    className="relative flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-border-base bg-surface-popover p-0 shadow-modal animate-in fade-in-0"
                                    data-track-location={TrackLocations.ChangelogDialog}
                                    data-testid="changelog-dialog"
                                >
                                    <DialogPrimitive.Title className="sr-only">Changelog</DialogPrimitive.Title>

                                    {/* Header */}
                                    <div className="grid grid-cols-[1fr_auto_1fr] items-center border-b border-border-subtle py-2 pl-3 pr-2">
                                        {changelog.date ? (
                                            <Text className="flex h-8 select-none items-center pl-[10px] text-sm text-content-muted">
                                                {changelog.date}
                                            </Text>
                                        ) : (
                                            <span />
                                        )}
                                        <Button variant="ghost" size="sm" asChild data-tracking-id="changelog-view-all">
                                            <a
                                                href={`${docsBaseUrl}/changelog`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-base text-content-primary"
                                            >
                                                Changelog
                                                <span className="text-xs text-content-muted">
                                                    <IconArrowUpRight size={12} style={{ marginTop: -2 }} />
                                                </span>
                                            </a>
                                        </Button>
                                        <DialogPrimitive.Close asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="ml-auto p-2 text-content-muted"
                                                aria-label="Close"
                                            >
                                                <IconCrossSmall />
                                            </Button>
                                        </DialogPrimitive.Close>
                                    </div>

                                    {/* Scrollable content */}
                                    <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
                                        <Text className="mb-3 text-xl font-semibold text-content-primary">
                                            {changelog.title}
                                        </Text>

                                        {/* Hero media — video for Mux URLs, static image otherwise */}
                                        {changelog.imageUrl && muxPlaybackId && (
                                            <div className="relative mb-4 aspect-video overflow-hidden rounded-lg">
                                                {/* Thumbnail stays visible until the video is ready */}
                                                {!videoReady && (
                                                    <img
                                                        src={changelog.imageUrl}
                                                        alt={changelog.title}
                                                        decoding="sync"
                                                        className="absolute inset-0 z-10 size-full object-cover"
                                                    />
                                                )}
                                                <VideoSection
                                                    key={openCountRef.current}
                                                    className="absolute inset-0 size-full rounded-none drop-shadow-none"
                                                    playbackId={muxPlaybackId}
                                                    metadataVideoTitle={changelog.title}
                                                    poster={changelog.imageUrl}
                                                    autoPlay="muted"
                                                    onCanPlay={handleVideoReady}
                                                    style={{ borderRadius: "0" }}
                                                />
                                            </div>
                                        )}
                                        {changelog.imageUrl && !muxPlaybackId && !imgFailed && (
                                            <div className="relative mb-4 aspect-video overflow-hidden rounded-lg">
                                                <img
                                                    src={changelog.imageUrl}
                                                    alt={changelog.title}
                                                    className="absolute inset-0 size-full rounded-lg object-cover"
                                                    decoding="sync"
                                                    onError={handleImgError}
                                                />
                                            </div>
                                        )}

                                        <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                                            {changelog.description}
                                        </ReactMarkdown>

                                        {changelog.sections && <ChangelogSectionsBlock sections={changelog.sections} />}
                                    </div>
                                </DialogPrimitive.Content>
                            </div>
                        </>
                    )}
                </DialogPrimitive.Portal>
            </DialogPrimitive.Root>
        </>
    );
};
